import { arrayMove } from '@dnd-kit/sortable';
import {
    HOME_BUILT_IN_SIZES,
    HOME_WIDGET_FORM_SIZES,
    type HomeBuiltIn,
    type HomeEntry,
    type HomeLayoutSave,
    type HomePlacement,
    type HomeWidgetSize,
} from 'types/homeWidgets';
import { t } from 'utils/getTranslations';

/** A built-in section's name: the wording of its own heading. */
export const BUILT_IN_TITLE: Record<HomeBuiltIn, string> = {
    MY_CASES: 'dashboard.my-cases',
    CONTINUE: 'home.continue',
    MY_TIME: 'home.my-time',
    MY_TIME_RECORDS: 'home.my-time-records',
    TO_REVIEW: 'home.to-review-or-approve',
    SAVED_FOR_LATER: 'create-dialog.saved.title',
};

export const SIZE_NAME: Record<HomeWidgetSize, string> = {
    '1x1': 'home.widgets.size.small',
    '1x2': 'home.widgets.size.medium',
    '2x2': 'home.widgets.size.wide',
    '1x4': 'home.widgets.size.tall',
    '2x4': 'home.widgets.size.large',
};

/**
 * The cells a size spans from two columns up. In one column every entry is one cell wide and its row is as tall as
 * its content.
 */
export const SIZE_SPAN: Record<HomeWidgetSize, string> = {
    '1x1': 'sm:col-span-1 sm:row-span-1',
    '1x2': 'sm:col-span-1 sm:row-span-2',
    '2x2': 'sm:col-span-2 sm:row-span-2',
    '1x4': 'sm:col-span-1 sm:row-span-4',
    '2x4': 'sm:col-span-2 sm:row-span-4',
};

/** In one column a widget keeps the height its rows give it on the grid, where a built-in list takes its content's. */
export const SIZE_HEIGHT: Record<HomeWidgetSize, string> = {
    '1x1': 'max-sm:h-30',
    '1x2': 'max-sm:h-63',
    '2x2': 'max-sm:h-63',
    '1x4': 'max-sm:h-129',
    '2x4': 'max-sm:h-129',
};

/** A size as it is written for people: `2×4`. */
export function sizeLabel(size: HomeWidgetSize): string {
    return size.replace('x', '×');
}

/** An entry's identity: a layout holds a built-in or a widget once. */
export function entryKey(placement: HomePlacement): string {
    return placement.kind === 'BUILT_IN' ? placement.builtIn : `widget-${placement.widget.id}`;
}

export function entryName(placement: HomePlacement): string {
    return placement.kind === 'BUILT_IN' ? t(BUILT_IN_TITLE[placement.builtIn]) : placement.widget.name;
}

/** The sizes an entry can take, smallest first. */
export function sizesOf(placement: HomePlacement): HomeWidgetSize[] {
    return placement.kind === 'BUILT_IN'
        ? HOME_BUILT_IN_SIZES[placement.builtIn]
        : HOME_WIDGET_FORM_SIZES[placement.widget.definition.form];
}

function indexOfKey(entries: HomeEntry[], key: string): number {
    return entries.findIndex((entry) => entryKey(entry) === key);
}

/** Moves an entry to a position in the list, which is all that places it: the grid flows the list. */
export function moveEntryTo(entries: HomeEntry[], key: string, to: number): HomeEntry[] {
    const from = indexOfKey(entries, key);
    return from === -1 || to === from || to < 0 || to >= entries.length ? entries : arrayMove(entries, from, to);
}

/** One place earlier (`-1`) or later (`1`); the same list at either end. */
export function moveEntryBy(entries: HomeEntry[], key: string, by: -1 | 1): HomeEntry[] {
    return moveEntryTo(entries, key, indexOfKey(entries, key) + by);
}

export function resizeEntry(entries: HomeEntry[], key: string, size: HomeWidgetSize): HomeEntry[] {
    return entries.map((entry) => (entryKey(entry) === key ? { ...entry, size } : entry));
}

export function removeEntry(entries: HomeEntry[], key: string): HomeEntry[] {
    return entries.filter((entry) => entryKey(entry) !== key);
}

/** An added entry lands last: at the size chosen for it, or at its smallest when none was. */
export function addEntry(entries: HomeEntry[], placement: HomePlacement, size = sizesOf(placement)[0]): HomeEntry[] {
    return [...entries, { ...placement, size }];
}

export function isOnHome(entries: HomeEntry[], placement: HomePlacement): boolean {
    return indexOfKey(entries, entryKey(placement)) !== -1;
}

/**
 * The cells a list takes on a grid of `columns`, packed as the grid packs it (`grid-flow-dense`): each entry in turn
 * takes the first place, row by row, where every cell it spans is free. `rows[row][column]` is true when taken.
 */
export function pack(entries: Pick<HomeEntry, 'size'>[], columns: number): boolean[][] {
    const rows: boolean[][] = [];
    for (const { size } of entries) {
        const [width, height] = size.split('x').map(Number);
        const span = Math.min(width, columns);
        const cells = (at: number) =>
            Array.from({ length: span * height }, (_, i) => [
                Math.floor(at / columns) + Math.floor(i / span),
                (at % columns) + (i % span),
            ]);
        let at = 0;
        // The corner first: most places tried are taken there, and checking it builds no list of cells.
        while (
            (at % columns) + span > columns ||
            rows[Math.floor(at / columns)]?.[at % columns] ||
            cells(at).some(([row, col]) => rows[row]?.[col])
        )
            at += 1;
        for (const [row, col] of cells(at)) (rows[row] ??= Array<boolean>(columns).fill(false))[col] = true;
    }
    return rows;
}

/** The gaps a list leaves on a grid of `columns`: the empty cells with a taken one below them in the same column. */
export function gapCount(entries: Pick<HomeEntry, 'size'>[], columns: number): number {
    const rows = pack(entries, columns);
    return rows.reduce(
        (gaps, row, at) =>
            gaps + row.filter((taken, col) => !taken && rows.slice(at + 1).some((below) => below[col])).length,
        0
    );
}

/**
 * Whether a change of layout leaves more gaps than the layout had, on two columns or on three; one column has none.
 * Counted, not placed: a layout that has gaps already takes any change that does not add to their number.
 */
export function addsGap(before: Pick<HomeEntry, 'size'>[], after: Pick<HomeEntry, 'size'>[]): boolean {
    return [2, 3].some((columns) => gapCount(after, columns) > gapCount(before, columns));
}

/** The body that saves a layout: a widget goes by its id. */
export function toLayoutSave(entries: HomeEntry[]): HomeLayoutSave {
    return {
        entries: entries.map((entry) =>
            entry.kind === 'BUILT_IN'
                ? { kind: entry.kind, builtIn: entry.builtIn, size: entry.size }
                : { kind: entry.kind, widgetId: entry.widget.id, size: entry.size }
        ),
    };
}
